import { ContactParams } from "app/models/contact-params";
import { DealParams } from "app/models/deal-params";
import { pricingGridMarginOrFeeTypeOptions, pricingGridTypeOptions, Option } from "app/models/dropdown-options";
import { EntityParams } from "app/models/entity-params";
import { HolidayCalendarParams } from "app/models/holiday-calendar-params";
import cleanUUID from "app/views/components/functions/cleanUUID";
import { removeExistingActives } from "app/views/components/functions/removeExistingActives";
import moment from "moment";

type CSVRow = Record<string, string | null | undefined>;

export function transformDealCSVToSubmission(
    csvRows: CSVRow[],
    deals: DealParams[],
    entities: EntityParams[],
    contacts: ContactParams[],
    holidayCalendars: HolidayCalendarParams[],
    businessDate: Date,
    transformValues: (values: any) => DealParams
): DealParams[] {

    function findEntityId(entityName: string) {
        return entities.find(
            entity => entity.entityName === entityName
        )?.id
    }
    function findHolidayId(holidayName: string) {
        return holidayCalendars.find(
            hc => hc.name === holidayName
        )?.id
    }

    function findContactsId(contactNames: string): string[] {
        if (!contactNames) return [];
        const names = contactNames.split("|").map(name => name.trim());
        return names
            .map(name => contacts.find(contact => contact.name === name)?.id)
            .filter((id): id is string => !!id)
            .map(cleanUUID);
    }

    const getMappedValue = (options: Option[], label: string | null | undefined): string => {
        const match = options.find((option) => option.label === label);
        return match ? match.value : '';
    };

    const processedRows = removeExistingActives(csvRows, deals);

    return processedRows.map((row) => {
        const dealNameFromCSV = row['Deal Name']?.trim()
        const isNameAutoGenerated = !dealNameFromCSV
        const updatedRow = {
            clientId: row["Client ID"] || '',
            id: deals.find(deal => deal?.id === row["ID"])?.id || null,
            // Construct dealName dynamically
            dealName: isNameAutoGenerated ? (() => {
                const borrowerName =
                    entities.find(entity => entity.entityName === row["Borrowers"]?.split("|")[0])?.entityName ?? '';

                const date = row["Start Date"] ? moment(row["Start Date"], "MM/DD/YYYY").format('YYYY-MMM').toUpperCase() : moment(businessDate).format('YYYY-MMM').toUpperCase();

                return `${borrowerName} (${date})`.trim();
            })() : dealNameFromCSV,
            isNameAutoGenerated: isNameAutoGenerated,
            systemId: row["System ID"] || null,
            cusip: row["CUSIP"] || '',
            isin: row["ISIN"] || '',
            lxId: row["LX ID"] || '',
            bbgId: row["BBG ID"] || '',
            approve: row["Approve"] === "Yes",

            adminEntity: row["Administrative Agent"] ? row["Administrative Agent"].split("|").map(findEntityId) : [],
            whiteLabelAgent: row["White Label Agent"] ? findEntityId(row["White Label Agent"]) || '' : '',
            currency: row["Currency"] || 'USD',
            originalAmount: row["Original Amount"] ? parseFloat(row["Original Amount"]) : 0,
            amount: row["Current Amount"] ? parseFloat(row["Current Amount"]) : 0,
            utilizedAmount: row["Utilized Amount"] ? parseFloat(row["Utilized Amount"]) : 0,

            startDate: row["Start Date"] ? (row["Start Date"]) : businessDate,
            maturity: row["Maturity Date"] ? (row["Maturity Date"]) : businessDate,
            precedentDate: row["Precedent Date"] ? (row["Precedent Date"]) : businessDate,
            closing: row["Closing Date"] ? (row["Closing Date"]) : businessDate,
            amendmentDate: row["Amendment Date"] ? (row["Amendment Date"]) : undefined,

            classification: row["Classification"] || null,
            borrowers: row["Borrowers"] ? row["Borrowers"].split("|").map(findEntityId) : [],
            guarantors: row["Guarantors"] ? row["Guarantors"].split("|").map(findEntityId) : [],
            holidayCalendar: row["Holiday Calendars"] ? row["Holiday Calendars"].split("|").map(findHolidayId) : [],

            isPricingGrid: row["Pricing Grid"] === "Yes" ? 'true' : 'false',
            pricingGrid: row["Pricing Grid"] === "Yes" ? [{
                pricingGridType: getMappedValue(pricingGridTypeOptions, row["Pricing Grid Type"]),
                marginOrFee: getMappedValue(pricingGridMarginOrFeeTypeOptions, row["Margin"]),
                levels: row["Levels"] ? row["Levels"].split("|") : []
            }] : [],

            opsAdmin: row["Administrative Agent Contact"] ? findContactsId(row["Administrative Agent Contact"]) : [],
            whiteLabelAgentContacts: row["White Label Agent Contact"] ? findContactsId(row["White Label Agent Contact"]) : [],
            fees: row["Fees"] ? row["Fees"].split("|") : [],
            allowNonProRataAssignments: row["Allow Non Pro Rata Assignments"] === "Yes" ? true : false,
        };

        const tranformedDeals = transformValues(updatedRow);
        return tranformedDeals;
    });
}
